/**
 * Canvas Awareness Store — Tier-4 state for ESA's canvas awareness.
 *
 * WHY a separate store (DR-1):
 * - Canvas writes, ESA reads — different ownership than esaDomainStore.
 * - High-frequency updates (row selection, filter changes) need isolated
 *   subscription scope so ESA panel UI doesn't re-render on every canvas event.
 * - Solution switch resets Ring 2+3 independently of ESA conversation state.
 *
 * WHY window singleton (DR-7):
 * - CapabilityTabsLayout runs inside the host CRE expose bundle, which is a
 *   separate Webpack chunk from the host main bundle. Zustand can instantiate
 *   twice across MF boundaries without a shared window key. This matches the
 *   pattern used by shellEventBus (window.__SHELL_EVENT_BUS__) and
 *   resolvingTabBridge (window.__RESOLVING_TAB__).
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools } from 'zustand/middleware';
import {
  DEFAULT_DATA_CONTEXT,
  DEFAULT_NAVIGATION,
  DEFAULT_VIEWPORT,
  DEFAULT_INTENT,
  type CanvasAwarenessState,
  type NavigationAwareness,
  type ViewportAwareness,
  type DataContext,
  type IntentAwareness,
  type DataResponse,
} from '@growthaxl/web-foundation';
import {
  MAX_DATA_RESPONSE_ROWS,
  MAX_SELECTED_ROWS,
} from '../../../constants/timing';
import { CANVAS_AWARENESS_STORE_WINDOW_KEY } from '../../../constants/storageKeys';

interface CanvasAwarenessActions {
  /** Update Ring 1 (navigation). Immediate — no debounce. */
  setNavigation(update: Partial<NavigationAwareness>): void;
  /** Update Ring 2 (viewport). Clears staleReason on first call after reset. */
  setViewport(update: Partial<ViewportAwareness>): void;
  /** Update Ring 2 data context sub-object. */
  setDataContext(update: Partial<DataContext>): void;
  /** Update Ring 3 (intent). Enforces caps on selectedRowIds. */
  setIntent(update: Partial<IntentAwareness>): void;
  /**
   * Solution switch: update Ring 1 with new solution, reset Ring 2+3,
   * set staleReason = 'solution_switched'. No-op if same solution.
   */
  resetForSolutionSwitch(newSolutionId: string, label?: string): void;
  /** Canvas unmounted: reset Ring 2+3, set staleReason = 'canvas_unmounted'. */
  markCanvasUnmounted(): void;
  /** Store a data pull response. Caps rows at MAX_DATA_RESPONSE_ROWS. */
  setDataResponse(response: DataResponse): void;
  /** Clear the data response (called after it's been sent in converse()). */
  clearDataResponse(): void;
  /** Full reset to defaults (e.g., logout). */
  reset(): void;
}

type StoreState = CanvasAwarenessState & CanvasAwarenessActions;

function buildStore() {
  return create<StoreState>()(
    devtools(
      immer((set) => ({
        // ── Default state ────────────────────────────────────────────────────
        navigation: { ...DEFAULT_NAVIGATION },
        // Spread DEFAULT_VIEWPORT then explicitly deep-copy dataContext using
        // DEFAULT_DATA_CONTEXT directly. Avoids a crash if DEFAULT_VIEWPORT
        // is transiently undefined during webpack module initialisation order
        // (circular import edge between host chunks and foundation barrel).
        viewport: {
          ...DEFAULT_VIEWPORT,
          dataContext: { ...DEFAULT_DATA_CONTEXT },
        },
        intent: { ...DEFAULT_INTENT },
        lastUpdatedAt: 0,
        staleReason: null,

        // ── Ring 1 ───────────────────────────────────────────────────────────
        setNavigation: (update) =>
          set((draft) => {
            Object.assign(draft.navigation, update);
            draft.lastUpdatedAt = Date.now();
          }),

        // ── Ring 2 ───────────────────────────────────────────────────────────
        setViewport: (update) =>
          set((draft) => {
            Object.assign(draft.viewport, update);
            // First viewport publish after a solution switch clears the stale marker
            if (draft.staleReason === 'solution_switched' || draft.staleReason === 'canvas_unmounted') {
              draft.staleReason = null;
            }
            draft.lastUpdatedAt = Date.now();
          }),

        setDataContext: (update) =>
          set((draft) => {
            Object.assign(draft.viewport.dataContext, update);
            draft.lastUpdatedAt = Date.now();
          }),

        // ── Ring 3 ───────────────────────────────────────────────────────────
        setIntent: (update) =>
          set((draft) => {
            if (update.selectedRowIds !== undefined) {
              draft.intent.selectedRowIds = update.selectedRowIds.slice(0, MAX_SELECTED_ROWS);
            }
            const { selectedRowIds: _skip, ...rest } = update;
            void _skip;
            Object.assign(draft.intent, rest);
            draft.lastUpdatedAt = Date.now();
          }),

        // ── Solution switch ───────────────────────────────────────────────────
        resetForSolutionSwitch: (newSolutionId, label) =>
          set((draft) => {
            // No-op if same solution to avoid unnecessary resets
            if (draft.navigation.solutionId === newSolutionId) return;

            // Ring 1: update with new solution, preserve other nav defaults
            draft.navigation = {
              ...DEFAULT_NAVIGATION,
              solutionId: newSolutionId,
              solutionLabel: label ?? null,
            };

            // Ring 2 + 3: full reset
            draft.viewport = {
              ...DEFAULT_VIEWPORT,
              dataContext: { ...DEFAULT_DATA_CONTEXT },
            };
            draft.intent = { ...DEFAULT_INTENT };

            draft.staleReason = 'solution_switched';
            draft.lastUpdatedAt = Date.now();
          }),

        // ── Canvas unmount ────────────────────────────────────────────────────
        markCanvasUnmounted: () =>
          set((draft) => {
            draft.viewport = {
              ...DEFAULT_VIEWPORT,
              dataContext: { ...DEFAULT_DATA_CONTEXT },
            };
            draft.intent = { ...DEFAULT_INTENT };
            draft.staleReason = 'canvas_unmounted';
            draft.lastUpdatedAt = Date.now();
          }),

        // ── Data response ─────────────────────────────────────────────────────
        setDataResponse: (response) =>
          set((draft) => {
            draft.intent.dataResponse = {
              ...response,
              rows: response.rows.slice(0, MAX_DATA_RESPONSE_ROWS),
            };
            draft.lastUpdatedAt = Date.now();
          }),

        clearDataResponse: () =>
          set((draft) => {
            draft.intent.dataResponse = null;
          }),

        // ── Full reset ────────────────────────────────────────────────────────
        reset: () =>
          set((draft) => {
            draft.navigation = { ...DEFAULT_NAVIGATION };
            draft.viewport = {
              ...DEFAULT_VIEWPORT,
              dataContext: { ...DEFAULT_DATA_CONTEXT },
            };
            draft.intent = { ...DEFAULT_INTENT };
            draft.lastUpdatedAt = 0;
            draft.staleReason = null;
          }),
      })),
      { name: 'CanvasAwareness' }
    )
  );
}

// ── Window singleton (DR-7) ────────────────────────────────────────────────────

const STORE_KEY = CANVAS_AWARENESS_STORE_WINDOW_KEY;

type AwarenessStore = ReturnType<typeof buildStore>;

declare global {
  interface Window {
    [STORE_KEY]?: AwarenessStore;
  }
}

/**
 * Get or create the awareness store singleton.
 * Safe to call from any MF bundle — always returns the same Zustand store instance.
 */
export function getOrCreateAwarenessStore(): AwarenessStore {
  if (!window[STORE_KEY]) {
    window[STORE_KEY] = buildStore();
  }
  return window[STORE_KEY]!;
}

export const useCanvasAwarenessStore: AwarenessStore = getOrCreateAwarenessStore();
